/**
 * Per-flow zustand store. One store per mounted flow, so the landing demo, the console preview
 * and the real page never share selections.
 */
import { createStore } from "zustand/vanilla";
import type { Draft, PublicPage, Tone } from "@/lib/api/types";
import { type Step, missingRequired, requiredNotice, step1Notice, toggleSelection } from "./logic";

export type Gen =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; drafts: Draft[] }
  | { kind: "manual"; reason: "quota" | "preview" | "self"; message?: string }
  | { kind: "error"; code: string; message: string; waitSeconds: number | null };

export interface ReviewState {
  step: Step;
  good: string[];
  bad: string[];
  answers: Record<string, string>;
  tone: Tone;
  notice: string;
  /** Question that blocked "next", so the view can move focus to it. */
  blockedQuestion: string | null;
  gen: Gen;
  choice: number;
  title: string;
  text: string;

  toggleGood(id: string): void;
  toggleBad(id: string): void;
  answer(questionId: string, optionId: string): void;
  setTone(tone: Tone): void;
  /** Validates the current step; returns true when it moved forward. */
  next(): boolean;
  back(): void;
  setNotice(notice: string): void;
  setGen(gen: Gen): void;
  pickDraft(index: number): void;
  setText(text: string): void;
  setTitle(title: string): void;
  tick(): void;
}

export type ReviewPageShape = Pick<PublicPage, "limits" | "questions">;

/** Server defaults (`PublicLimits`): 3 good, 2 bad. */
export function limitsOf(page: Pick<PublicPage, "limits">): { good_max: number; bad_max: number } {
  return { good_max: page.limits?.good_max ?? 3, bad_max: page.limits?.bad_max ?? 2 };
}

export function createReviewStore(page: ReviewPageShape) {
  return createStore<ReviewState>()((set, get) => ({
    step: 1,
    good: [],
    bad: [],
    answers: {},
    tone: "calm",
    notice: "",
    blockedQuestion: null,
    gen: { kind: "idle" },
    choice: 0,
    title: "",
    text: "",

    toggleGood(id) {
      const r = toggleSelection(get().good, id, limitsOf(page).good_max, "good");
      set({ good: r.list, notice: r.notice });
    },
    toggleBad(id) {
      const r = toggleSelection(get().bad, id, limitsOf(page).bad_max, "bad");
      set({ bad: r.list, notice: r.notice });
    },
    answer(questionId, optionId) {
      const blocked = get().blockedQuestion === questionId;
      set({
        answers: { ...get().answers, [questionId]: optionId },
        ...(blocked ? { blockedQuestion: null, notice: "" } : {}),
      });
    },
    setTone(tone) {
      set({ tone });
    },
    next() {
      const s = get();
      if (s.step === 1) {
        const n = step1Notice(s.good);
        if (n) {
          set({ notice: n });
          return false;
        }
        set({ step: 2, notice: "" });
        return true;
      }
      if (s.step === 2) {
        const q = missingRequired(page.questions, s.answers);
        if (q) {
          set({ notice: requiredNotice(q), blockedQuestion: q.id });
          return false;
        }
        set({ step: 3, notice: "", blockedQuestion: null });
        return true;
      }
      return false;
    },
    back() {
      const s = get();
      if (s.step > 1) set({ step: (s.step - 1) as Step, notice: "", blockedQuestion: null });
    },
    setNotice(notice) {
      set({ notice });
    },
    setGen(gen) {
      if (gen.kind === "ready") {
        const first = gen.drafts[0];
        set({ gen, choice: 0, text: first?.text ?? "", title: first?.title ?? "" });
      } else {
        set({ gen });
      }
    },
    pickDraft(index) {
      const g = get().gen;
      if (g.kind !== "ready") return;
      const d = g.drafts[index];
      if (!d) return;
      set({ choice: index, text: d.text, title: d.title ?? "" });
    },
    setText(text) {
      set({ text });
    },
    setTitle(title) {
      set({ title });
    },
    tick() {
      const g = get().gen;
      if (g.kind === "error" && g.waitSeconds !== null && g.waitSeconds > 0) {
        set({ gen: { ...g, waitSeconds: g.waitSeconds - 1 } });
      }
    },
  }));
}

export type ReviewStore = ReturnType<typeof createReviewStore>;
